export const METRIC_THREAD_PATH = '/threads/metric';
export const METRIC_THREAD_URL = `https://asijolab.com${METRIC_THREAD_PATH}`;
export const METRIC_THREAD_TITLE = 'Metric Thread Calculator – ISO Dimensions & Tolerances | ASiJoLab';
export const METRIC_THREAD_DESCRIPTION = 'Free ISO metric thread calculator. Find internal and external thread dimensions, pitch, major, minor and pitch diameters, and 6H/6g tolerance limits.';
export const METRIC_THREAD_H1 = 'ISO Metric Thread Calculator';

export const METRIC_THREAD_OPEN_GRAPH = Object.freeze({
  title: METRIC_THREAD_TITLE,
  description: METRIC_THREAD_DESCRIPTION,
  url: METRIC_THREAD_URL,
  type: 'website',
  siteName: 'ASiJoLab',
});

export const METRIC_THREAD_TWITTER = Object.freeze({
  card: 'summary',
  title: METRIC_THREAD_TITLE,
  description: METRIC_THREAD_DESCRIPTION,
});

export const METRIC_THREAD_STRUCTURED_DATA = Object.freeze({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: METRIC_THREAD_H1,
  description: METRIC_THREAD_DESCRIPTION,
  url: METRIC_THREAD_URL,
  applicationCategory: 'EngineeringApplication',
  operatingSystem: 'Any',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
});

export const METRIC_THREAD_GUIDE_SECTIONS = Object.freeze([
  {
    title: 'What is an ISO metric thread?',
    paragraphs: ['An ISO general-purpose metric screw thread uses the M thread form and a 60° included angle. Its basic and design profiles are defined by ISO 68-1, while diameter-and-pitch combinations are organized by ISO 261.'],
  },
  {
    title: 'Coarse and fine pitch',
    paragraphs: ['A coarse pitch is the usual default pitch for a nominal diameter. Fine pitches have a smaller distance between adjacent thread crests and may be selected when the applicable design, wall thickness or adjustment requirement calls for them. The calculator only offers combinations supported by its backend catalog.'],
  },
  {
    title: 'Internal and external threads',
    paragraphs: ['Internal threads are produced in holes, while external threads are produced on shafts, bolts or studs. ISO notation uses uppercase tolerance-position letters for internal threads and lowercase letters for external threads; the calculator keeps their diameter limits and profile geometry separate.'],
  },
  {
    title: 'Major, pitch and minor diameters',
    paragraphs: ['The major diameter is the largest diameter of the basic thread profile. The pitch diameter is the diameter where thread and space widths are equal and is central to flank fit. The minor diameter is the smallest basic-profile diameter; external root design diameter d3 is presented separately from the basic d1 reference where applicable.'],
  },
  {
    title: 'Tolerance classes such as 6H and 6g',
    paragraphs: ['A tolerance class combines a numerical tolerance grade with a letter identifying the tolerance position. A common pairing is 6H for an internal thread and 6g for an external thread. Available classes remain limited to those returned by the calculator backend and should be checked against the applicable product specification.'],
  },
  {
    title: 'How to read a metric thread designation',
    paragraphs: ['In M6 × 1 - 6H, M identifies an ISO metric thread, 6 is the nominal diameter in millimetres, 1 is the pitch in millimetres and 6H is the internal-thread tolerance class. An external counterpart may use a class such as 6g.'],
  },
  {
    title: 'Basic dimensions and tolerance limits',
    paragraphs: ['Basic dimensions describe the theoretically exact thread profile before tolerance is applied. Tolerance limits define the permitted minimum and maximum sizes for manufacturing and inspection. This calculator displays both concepts separately so a reference profile value is not mistaken for a production limit.'],
  },
]);

export const METRIC_THREAD_STANDARDS = Object.freeze([
  { name: 'ISO 68-1', description: 'basic and design profiles', url: 'https://www.iso.org/standard/85107.html' },
  { name: 'ISO 261', description: 'general plan and diameter/pitch series', url: 'https://www.iso.org/standard/4165.html' },
  { name: 'ISO 724', description: 'basic dimensions', url: 'https://www.iso.org/standard/85104.html' },
  { name: 'ISO 965-1', description: 'tolerance-system principles and basic data', url: 'https://www.iso.org/standard/87889.html' },
]);
